import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessagesSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useChat } from "@/context/ChatContext";
import { hasRole } from "@/lib/roles";
import { ConversationList } from "@/components/chat/ConversationList";
import { ContactPicker } from "@/components/chat/ContactPicker";
import { MessageThread } from "@/components/chat/MessageThread";

export default function Chat() {
    const { me, online, subscribe, refreshUnread } = useChat();
    const [params, setParams] = useSearchParams();
    const [convs, setConvs] = useState([]);
    const [picker, setPicker] = useState(null);
    const activeId = params.get("c") || "";

    const load = useCallback(() => api.get("/chat/conversations").then(({ data }) => setConvs(data || [])).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => subscribe((m) => { if (["message", "conversation"].includes(m.type)) load(); }), [subscribe, load]);

    const open = (id) => setParams(id ? { c: id } : {});
    const onRead = useCallback(() => { setConvs((cs) => cs.map((c) => (c.id === activeId ? { ...c, unread: 0 } : c))); refreshUnread(); }, [activeId, refreshUnread]);
    const active = convs.find((c) => c.id === activeId);
    const canGroup = hasRole(me, "admin", "hr");
    const deptGroups = async () => {
        try {
            const { data } = await api.post("/chat/department-groups");
            toast.success(data.created.length ? `Created ${data.created.length} department group${data.created.length > 1 ? "s" : ""}: ${data.created.join(", ")}` : "Every department with 2+ staff already has a group");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create department groups");
        }
    };
    const boxRef = useRef(null);
    const [height, setHeight] = useState("100dvh");
    useLayoutEffect(() => {
        const fit = () => boxRef.current && setHeight(`${window.innerHeight - boxRef.current.getBoundingClientRect().top - window.scrollY}px`);
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(document.body);
        window.addEventListener("resize", fit);
        return () => { ro.disconnect(); window.removeEventListener("resize", fit); };
    }, []);

    return (
        <div ref={boxRef} style={{ height }} className="flex bg-white" data-testid="chat-page">
            <aside className={`${activeId ? "hidden lg:flex" : "flex"} w-full lg:w-[340px] shrink-0 flex-col border-r border-ink/10`}>
                <ConversationList convs={convs} activeId={activeId} online={online} onOpen={open}
                    onNewChat={() => setPicker("direct")} onNewGroup={() => setPicker("group")} onDeptGroups={deptGroups} canGroup={canGroup} />
            </aside>
            <section className={`${activeId ? "flex" : "hidden lg:flex"} flex-1 min-w-0 flex-col`}>
                {active ? (
                    <MessageThread key={active.id} conv={active} onBack={() => open("")} onRead={onRead}
                        onEditGroup={canGroup ? () => setPicker("edit") : undefined} />
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-10 bg-[#F4F6FB]" data-testid="chat-empty-state">
                        <div className="w-16 h-16 rounded-2xl bg-klein/10 text-klein flex items-center justify-center"><MessagesSquare className="w-8 h-8" /></div>
                        <div className="mt-4 font-display font-bold text-xl text-ink">Talk to your team</div>
                        <p className="mt-1 text-sm text-ink/55 max-w-sm">Message colleagues, share photos and files, and start voice or video calls with anyone who's online.</p>
                    </div>
                )}
            </section>
            <ContactPicker open={!!picker} mode={picker === "direct" || !picker ? "direct" : "group"} online={online} onClose={() => setPicker(null)}
                group={picker === "edit" ? active : null}
                onDone={(c) => { setPicker(null); load().then(() => open(c.id)); }} />
        </div>
    );
}
